import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoImg from '../assets/logo.webp';
import './Navbar.css';

import { supabase } from '../supabaseClient';

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [selectedBrochure, setSelectedBrochure] = useState(null);
  const [brochuresList, setBrochuresList] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '', address: '' });

  useEffect(() => {
    const fetchBrochures = async () => {
      const { data, error } = await supabase
        .from('brochures')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) {
        setBrochuresList(data);
      }
    };
    fetchBrochures();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'apartments', label: 'Apartments' },
    { id: 'villas', label: 'Villas' },
    { id: 'plots', label: 'Plots' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'farmlands', label: 'Farm Lands' },
    { id: 'loans', label: 'Loans' },
    { id: 'contact', label: 'Contact' }
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleBrochureClick = (brochure) => {
    setSelectedBrochure(brochure);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // 1. Open WhatsApp link
    const text = `Hi, I am interested in ${selectedBrochure.title} brochure. Here are my details:%0A- Name: ${formData.name}%0A- Email: ${formData.email}%0A- Mobile: ${formData.mobile}%0A- Address: ${formData.address}`;
    const whatsappUrl = `https://wa.me/919099999149?text=${text}`;
    window.open(whatsappUrl, '_blank');

    // 2. Transition state to allow downloading
    setWhatsappSent(true);
  };

  const handleFinalDownload = () => {
    // Download file automatically
    const link = document.createElement('a');
    link.href = selectedBrochure.file_url;
    link.setAttribute('download', `${selectedBrochure.title.replace(/\s+/g, '_')}_Brochure.pdf`);
    // Add target blanket for external URLs to trigger download/viewing
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Close modal after short delay
    setTimeout(() => {
      closeBrochureModal();
    }, 1500);
  };

  const closeBrochureModal = () => {
    setBrochureModalOpen(false);
    setSelectedBrochure(null);
    setWhatsappSent(false);
    setFormData({ name: '', email: '', mobile: '', address: '' });
  };

  return (
    <header className={`main-header ${location.pathname === '/' ? 'home-theme' : 'page-theme'} ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Logo Branding */}
        <div className="logo-brand" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
          <img src={logoImg} alt="Skyon Logo" className="navbar-logo" />
          <div className="logo-text">
            <span className="logo-title">SKYON</span>
            <span className="logo-subtitle">INFRA DEVELOPERS</span>
          </div>
        </div>

        {/* Desktop Navigation Links */}
        <nav className="desktop-nav">
          <ul className="nav-list">
            {navItems.map((item) => (
              <li key={item.id}>
                <NavLink
                  to={item.id === 'home' ? '/' : `/${item.id}`}
                  className={({ isActive }) => `nav-link-btn ${isActive ? 'active' : ''}`}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {item.label}
                  {location.pathname === (item.id === 'home' ? '/' : `/${item.id}`) && <span className="active-dot"></span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Far Right Action Icons */}
        <div className="navbar-right-actions">
          {/* Download Brochure button replacing Enquire Now */}
          <button className="btn btn-primary nav-cta" onClick={() => setBrochureModalOpen(true)}>
            Download Brochure
          </button>

          {/* Hamburger Mobile Menu Toggle Button */}
          <button
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <div className={`mobile-nav-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-list">
          {navItems.map((item) => (
            <li key={item.id}>
              <NavLink
                to={item.id === 'home' ? '/' : `/${item.id}`}
                className={({ isActive }) => `mobile-nav-link-btn ${isActive ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
          <li>
            <button className="btn btn-primary w-full mobile-cta-btn" onClick={() => setBrochureModalOpen(true)}>
              Download Brochure
            </button>
          </li>
        </ul>
      </div>

      {/* Interactive Brochure Download Modal */}
      {brochureModalOpen && (
        <div className="brochure-modal-overlay flex-center">
          <div className="premium-card brochure-modal">
            <button className="close-modal-btn" onClick={closeBrochureModal}>×</button>
            
            {whatsappSent ? (
              <div className="modal-content-wrap text-center" style={{ padding: '20px' }}>
                <span className="success-check animate-float" style={{ fontSize: '3rem', marginBottom: '10px', display: 'inline-block' }}>✅</span>
                <h3 style={{ marginBottom: '10px' }}>Message Sent?</h3>
                <p style={{ fontSize: '0.95rem', color: 'var(--color-text-muted)', marginBottom: '25px', lineHeight: '1.5' }}>
                  If you have successfully sent the WhatsApp message, click below to finally download your brochure.
                </p>
                <button className="btn btn-primary w-full" onClick={handleFinalDownload}>
                  Download Brochure PDF
                </button>
              </div>
            ) : selectedBrochure ? (
              <div className="modal-content-wrap">
                <h3>Request Brochure</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginBottom: '20px' }}>
                  Please fill out the form below to connect with our team and receive the {selectedBrochure.title} brochure.
                </p>
                <form onSubmit={handleFormSubmit} className="brochure-form" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <input type="text" name="name" placeholder="Full Name" required value={formData.name} onChange={handleFormChange} style={{ padding: '10px', borderRadius: '5px', border: '1px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text)' }} />
                  <input type="email" name="email" placeholder="Email Address" required value={formData.email} onChange={handleFormChange} style={{ padding: '10px', borderRadius: '5px', border: '1px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text)' }} />
                  <input type="tel" name="mobile" placeholder="Mobile Number" required value={formData.mobile} onChange={handleFormChange} style={{ padding: '10px', borderRadius: '5px', border: '1px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text)' }} />
                  <textarea name="address" placeholder="Your Address" required value={formData.address} onChange={handleFormChange} rows="3" style={{ padding: '10px', borderRadius: '5px', border: '1px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text)', resize: 'vertical' }}></textarea>
                  <button type="submit" className="btn btn-primary w-full" style={{ marginTop: '10px' }}>
                    Send on WhatsApp
                  </button>
                  <button type="button" onClick={() => setSelectedBrochure(null)} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: '0.85rem', cursor: 'pointer', marginTop: '10px' }}>
                    ← Back to Brochures list
                  </button>
                </form>
              </div>
            ) : (
              <div className="modal-content-wrap">
                <h3>Download Project Brochure</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)', marginBottom: '20px' }}>
                  Select the development you are interested in to instantly download its digital brochure.
                </p>
                <div className="project-downloads-list" style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' }}>
                  {brochuresList.map((brochure) => (
                    <button 
                      key={brochure.id} 
                      className="brochure-download-row"
                      onClick={() => handleBrochureClick(brochure)}
                    >
                      <span className="doc-icon">📄</span>
                      <div className="doc-info text-left">
                        <span className="doc-title">{brochure.title}</span>
                      </div>
                      <span className="download-arrow">→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
